import React from 'react'
import PropTypes from 'prop-types'
import {Box, useRecipe} from '@chakra-ui/react3'
import {hasValidString} from '../../../utils/utils'
import RichText from '../../cms/rich-text'
import separatorRecipe from './index.recipe'

/**
 * Renders a navigation separator as either a spacer, divider line, or text.
 * Intended for use inside the Xleap navigation component tree.
 *
 * @param {Object} props - Component properties.
 * @param {string} [props.text] - CMS-provided text for the flat separator shape.
 * @param {Object} [props.separator] - CMS-provided nested separator config.
 * @param {string} [props.separator.text] - CMS-provided text for the nested separator shape.
 * @param {'divider'|'text'|'space'} [props.variant] - Visual separator variant.
 * @returns {JSX.Element|null} Rendered separator element.
 */
const resolveSeparatorVariant = ({separator, separatorText, variant}) => {
    if (separator?.variant) return separator.variant
    if (variant) return variant
    if (hasValidString(separatorText)) return 'text'

    return separatorRecipe.defaultVariants.variant
}

const NavigationSeparator = ({separator, text, variant}) => {
    const recipe = useRecipe({recipe: separatorRecipe})
    const separatorText = separator?.text ?? text
    const separatorVariant = resolveSeparatorVariant({separator, separatorText, variant})
    const styles = recipe({variant: separatorVariant})

    if (separatorVariant === 'text' && !hasValidString(separatorText)) return null

    if (separatorVariant === 'divider') {
        return <Box as="hr" css={styles} />
    }

    if (separatorVariant === 'space') {
        return <Box aria-hidden="true" css={styles} data-testid="navigation-separator-space" />
    }

    return (
        <RichText as="p" css={styles}>
            {separatorText}
        </RichText>
    )
}

NavigationSeparator.propTypes = {
    separator: PropTypes.shape({
        text: PropTypes.string,
        variant: PropTypes.oneOf(Object.keys(separatorRecipe.variants.variant))
    }),
    text: PropTypes.string,
    variant: PropTypes.oneOf(Object.keys(separatorRecipe.variants.variant))
}

export default NavigationSeparator
